import { RootState } from '@frontend/core'
import { createSelector } from '@reduxjs/toolkit'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useAppSelector } from '../../store/hooks'

// Memoized selectors for websocket URLs to prevent unnecessary re-renders
// These selectors only change when the actual URL string changes, not when
// other parts of the servers state (like connections) change

export const selectMetricsServerWebsocketUrl = createSelector(
  [
    (state: RootState) => state.servers.urls.metricsServer?.websocketUrl,
    (state: RootState) => state.regions.selectedId
  ],
  (websocketUrl, region) =>
    (websocketUrl ?? '').replace('{region}', region ?? '')
)

// Hook for raw websocket URL - chooses between metricsServer and streaming
// based on pitchSide config (which is static and doesn't change at runtime)
export const useRawWebsocketUrl = (): [string, string] => {
  const { sessionEventId } = useParams()
  const pitchSide = useAppSelector((state) => state.app.config.pitchSide)
  const metricsServerWs = useAppSelector(
    (state) => state.servers.urls.metricsServer?.websocketUrl
  )
  const streamingWs = useStreamingWebsocketUrl()

  return useMemo(
    () =>
      pitchSide
        ? [metricsServerWs, 'raw']
        : [streamingWs, `ws/session/${sessionEventId}/livePosition`],
    [pitchSide, metricsServerWs, streamingWs, sessionEventId]
  )
}

export const useStreamingWebsocketUrl = (): string => {
  const region = useAppSelector((state) => state.regions.selectedId)
  const streamingWs = (
    useAppSelector((state) => state.servers.urls.streaming?.websocketUrl) ?? ''
  ).replace('{region}', region ?? '')

  return useMemo(() => streamingWs, [streamingWs, region])
}

// Selector for metrics server connection status - returns only the boolean
// instead of the entire connections object to prevent unnecessary re-renders
export const selectMetricsServerConnected = (state: RootState) =>
  state.servers.connections.metricsServer?.connected

// Selector for full metrics server connection object
// Returns both 'connected' and 'hasMadeSuccessfulConnection'
export const selectMetricsServerConnection = (state: RootState) =>
  state.servers.connections.metricsServer

// Selector for full metrics server config object
export const selectMetricsServerConfig = (state: RootState) =>
  state.servers.configs.metricsServer

export const selectLocationName = (state: RootState) =>
  state.metricsServer?.metricsServerState?.config?.LocationName || ''
